import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBranches } from 'services';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { warehouseI } from 'interfaces';
import { useTranslation } from 'react-i18next';
import { useWarehouseStore } from '../store/useWarehouseStore';
import {
  createWarehouse,
  updateWarehouse,
} from 'services/service.cruds/WarehouseServices/WarehouseService';
import { NotificationType, showNotifications } from 'helpers/toas_notification';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDefaultValueForSelect, validateYupStringWithSpecialCharacters } from 'helpers/functionsHelpers';
import { OptionSelect } from 'Common/Components';

export const useAddWarehouse = () => {
  const navigate = useNavigate();
  const [isSubmitOption, setIsSubmitOption] = useState(false);
  const [valueBranch, setValueBranch] = useState<OptionSelect>();
  const { t } = useTranslation();
  const [optionBranch, setOptionBranch] = useState<OptionSelect[]>([
    {
      label: t('selectComponent.defaultValue'),
      value: '0',
      isDisabled: true,
    },
  ]);
  const {
    handleCloseModalWarehouse,
    wharehouse,
    isEdit,
    setIsEdit,
    setWarehouse,
  } = useWarehouseStore();
  const queryClient = useQueryClient();

  const initialValues = {
    description: wharehouse?.description ?? '',
    main_warehouses: wharehouse?.mainWarehouse ?? '',
    branch_id: wharehouse?.branch.id ?? '',
  };

  const validationSchema = Yup.object().shape({
    description: validateYupStringWithSpecialCharacters(t, true),
    branch_id: Yup.number().required(t('validationForms.required')),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, {setSubmitting, setErrors}) => {
      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        setSubmitting(false);

        return;
      }
      setSubmitting(true); 

      const warehouses: warehouseI = {
        id: isEdit ? wharehouse?.id : undefined,
        description: values.description,
        main_warehouses: values.main_warehouses,
        branch_id: values.branch_id,
      };

      try {
        if (isEdit && wharehouse) {
          await handleEditWarehouse(warehouses, wharehouse.id);
        } else {
          await handleCreateWarehouse(warehouses);
        }

      } catch(error) {
        showNotifications(NotificationType.ERROR, t("notificationsMessages.error") + error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { data: branches } = useQuery({
    queryKey: ['getBranchesByCompany'],
    queryFn: () => getBranches(),
  });
  useEffect(() => {
    if (branches) {
      const options: OptionSelect[] = [];

      branches.map((branch) => {
        options.push({
          label: branch.description,
          value: branch.id.toString(),
        });
      });

      setOptionBranch(options);
    }
  }, [branches]);
  
  useEffect(() => {
      const defaultValueFind = getDefaultValueForSelect(
        optionBranch,
        formik.values.branch_id.toString()
      );
  
      setValueBranch(defaultValueFind);
    }, [formik.values.branch_id, optionBranch]);

  const handleCreateWarehouse = async (warehouses: warehouseI) => {
    try {
      const response = await createWarehouse(warehouses);

      if (response.code === '200') {
        showNotifications(
          NotificationType.SUCCESS,
          t('notificationsMessages.successAdd')
        );
        queryClient.invalidateQueries({
          queryKey: ['administration-service-warehouses'],
        });
        setWarehouse(null);
        handleCloseModalWarehouse();
        formik.resetForm();
      } else {
        setWarehouse(null);
        showNotifications(
          NotificationType.ERROR,
          t('notificationsMessages.error') + response.message
        );
      }
    } catch (error) {
      setWarehouse(null);
      showNotifications(
        NotificationType.ERROR,
        t('notificationsMessages.error') + error
      );
    }
  };

  const handleEditWarehouse = async (warehouses: warehouseI, id: number) => {
    try {
      if (!wharehouse) return;

      const response = await updateWarehouse(
        id,
        warehouses
      );

      if (response.code === '200') {
        showNotifications(
          NotificationType.SUCCESS,
          t('notificationsMessages.success')
        );
        setIsEdit(false);
        setWarehouse(null);
        queryClient.invalidateQueries({
          queryKey: ['administration-service-warehouses'],
        });
        handleCloseModalWarehouse();
      } else {
        setIsEdit(false);
        setWarehouse(null);
        showNotifications(
          NotificationType.ERROR,
          t('notificationsMessages.error') + response.message
        );
      }
    } catch (error) {
      setIsEdit(false);
      setWarehouse(null);
      showNotifications(
        NotificationType.ERROR,
        t('notificationsMessages.error') + error
      );
    }
  };

  return {
    navigate,
    isSubmitOption,
    formik,
    optionBranch,
    valueBranch,
  };
};
